import { Clock } from 'lucide-react';
import type { Event } from '@casetrace/shared';
import type { LucideIcon } from 'lucide-react';
import {
  isAttachmentOnlyPreview,
  isSelfNamedDocument,
  type EventAttachment,
} from '../event-attachments';
import { AttachmentFileTypeIcon } from '../attachment-filetype-icon';

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

interface EventCardCollapsedProps {
  event: Event;
  isSelected: boolean;
  color: string;
  label: string;
  Icon: LucideIcon;
  attachmentsLength: number;
  attachmentsLabel: string;
  attachments: EventAttachment[];
  onSelect: (event: Event) => void;
  onDeselect: () => void;
}

function attachmentKey(attachment: EventAttachment, index: number): string {
  return attachment.sourceItemId ?? attachment.externalId ?? attachment.fileName ?? String(index);
}

function AttachmentTypeIcons({ attachments }: { attachments: EventAttachment[] }) {
  const visible = attachments.slice(0, 4);
  const hiddenCount = Math.max(0, attachments.length - visible.length);

  return (
    <span className="inline-flex items-center gap-1">
      {visible.map((attachment, index) => (
        <AttachmentFileTypeIcon key={attachmentKey(attachment, index)} attachment={attachment} />
      ))}
      {hiddenCount > 0 && <span className="text-[10px] text-text-tertiary">+{hiddenCount}</span>}
    </span>
  );
}

export function EventCardCollapsed({
  event,
  isSelected,
  color,
  label,
  Icon,
  attachmentsLength,
  attachmentsLabel,
  attachments,
  onSelect,
  onDeselect,
}: EventCardCollapsedProps) {
  const hidePreviewLine = isAttachmentOnlyPreview(event.contentPreview, attachments);
  const hideAttachmentFileName = isSelfNamedDocument(event, attachments);

  return (
    <button
      className={`w-full px-3 py-2.5 text-left transition-colors duration-150 ${
        !isSelected ? 'hover:border-white/[0.14] hover:bg-white/4' : ''
      }`}
      onClick={() => (isSelected ? onDeselect() : onSelect(event))}
    >
      <div className="flex items-center gap-2">
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-text-secondary">
          <Clock size={10} className="text-text-tertiary" />
          {formatTime(event.timestamp)}
        </span>
        <div
          className="flex items-center gap-1.5 rounded px-2 py-0.5"
          style={{ background: `${color}30` }}
        >
          <Icon size={11} style={{ color }} />
          <span className="text-[11px] font-semibold leading-none" style={{ color }}>
            {label}
          </span>
        </div>
        {attachmentsLength > 0 && attachmentsLength > 1 && (
          <div
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold"
            style={{ background: `${color}22`, outline: `1px solid ${color}55`, color }}
            title={attachmentsLabel}
            aria-label={attachmentsLabel}
          >
            <span>{attachmentsLength}</span>
          </div>
        )}
      </div>

      <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm font-medium text-text-primary">
        {hideAttachmentFileName && !isSelected && <AttachmentTypeIcons attachments={attachments} />}
        <span className="truncate">{event.title}</span>
      </p>

      {attachmentsLength > 0 && !hideAttachmentFileName && (
        <p className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
          {!isSelected && <AttachmentTypeIcons attachments={attachments} />}
          <span className="truncate">{attachmentsLabel}</span>
        </p>
      )}

      {!isSelected && event.contentPreview && !hidePreviewLine && (
        <p className="mt-0.5 line-clamp-2 text-xs text-text-secondary">{event.contentPreview}</p>
      )}
    </button>
  );
}
